import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { useUnsavedChanges } from "../../../components/layout/UnsavedChanges";
import {
  Button,
  EmptyState,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { ROUTES, clientDetailPath } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";
import AddClientForm from "../components/AddClientForm";
import ClientDetailSkeleton from "../components/ClientDetailSkeleton";
import { fullNameOf } from "../clientMapping";
import { toClientForm, toCreateClientPayload } from "../clientPayload";
import { useClient } from "../hooks/useClient";
import { useClientLookups } from "../hooks/useClientLookups";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { updateClient } from "../services/clientsService";
import { NewClient } from "../types/clients.types";
import { validateNewClient } from "../validation";

/**
 * The Add client form, opened on a client who already exists.
 *
 * The same form component, the same validation and the same payload builder —
 * only the answers it starts with and where it goes afterwards differ. Two
 * screens sharing one form is what keeps a field added to the add screen from
 * quietly going missing from the edit one.
 *
 * The record is fetched here rather than handed over by the detail screen: an
 * edit reached by its own URL — a reload, a bookmark, a link — has to fill the
 * form just the same.
 */
const EditClientPage: React.FC = () => {
  // Enciphered in the path — see `routeIds`. A token that does not decode is
  // "", which `useClient` already reports as a missing client.
  const { clientId: token = "" } = useParams();
  const clientId = decodeRouteId(token);

  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  const { client: record, status: loadStatus, reload } = useClient(clientId);
  const lookups = useClientLookups();

  // Read before the returns below, since a hook cannot sit behind one.
  const phase = useLoadPhase(loadStatus === "loading");

  // `undefined` until the record lands, which is what the effect below fills.
  // Held here rather than derived from the record on every render: from the
  // first keystroke this is what the form holds, and the record is only where
  // it started.
  const [client, setClient] = useState<NewClient | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setClient(toClientForm(record));
  }, [record]);

  // What the form opened with, which is what an edit is measured against. The
  // record itself rather than a second copy of the form state: it is the same
  // mapping the effect above seeds from, so the two cannot drift, and `record`
  // is a fresh object only when a fetch answered.
  const opened = useMemo(() => (record ? toClientForm(record) : null), [record]);

  // Worked out up here rather than beside the form below, since the prompt is
  // registered above the returns and has to know whether this form can be sent
  // as it stands. Nothing to check while the record is still on its way.
  const errors = client ? validateNewClient(client) : {};
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  // Above the returns below, since a hook cannot sit behind one — and true only
  // once there is a record to have edited, so the wait and the not-found screen
  // hold nothing.
  useUnsavedChanges(
    !!client && !!opened && JSON.stringify(client) !== JSON.stringify(opened),
    {
      // The add screen's wording, put in the tense this screen is in: nothing
      // is being entered here, an existing record is being changed — and
      // "Save & add" would name something this form does not do.
      title: "Unsaved client details",
      body: "You’ve changed this client’s details but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save changes",
    },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    {
      canSave: valid && !saving,
      save: () => commit(),
      report: () => problems.current?.(),
    }
  );

  const handleChange = (patch: Partial<NewClient>) =>
    setClient((current) => (current ? { ...current, ...patch } : current));

  /**
   * Sends the edit and says whether it landed, leaving where to go next to
   * whoever asked for it: the Save row goes back to the record, the rail's
   * prompt carries on to wherever it was pointing. It must not navigate itself,
   * or the prompt's answer would take somebody somewhere they did not ask for.
   */
  const commit = async (): Promise<boolean> => {
    if (saving || !client || !record) return false;

    // The dropdowns hold names; the endpoint takes the ids behind them. A name
    // with no id left to match means a catalog list changed while this form
    // sat open — worth naming the field rather than sending a broken body.
    const result = toCreateClientPayload(client, lookups);
    if (!result.ok) {
      toast.error(
        "Some options are out of date",
        `Pick ${result.unresolved.join(" and ")} again.`
      );
      return false;
    }

    setSaving(true);

    try {
      await updateClient(getToken, record.publicId, result.payload);
    } catch (error) {
      toast.error(
        "Could not save these changes",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("updating a client failed:", error);
      // Left on the form with every edit still in it, which is the only place
      // they can be corrected and sent again.
      setSaving(false);
      return false;
    }

    const name = `${client.firstName} ${client.lastName}`.trim();
    toast.success("Client updated", `${name}'s details have been saved.`);

    return true;
  };

  const handleSave = async () => {
    if (!(await commit()) || !record) return;

    // Back to the record itself, which refetches as it mounts — so what is
    // read there is what the backend stored rather than what was typed here.
    // On the Details tab specifically: that is the tab this form was reached
    // from, and the unqualified path would otherwise open on Plans.
    navigate(clientDetailPath(record.publicId, "details"));
  };

  // The record has to be in before the form can open on it, so the wait is the
  // detail screen's own. `!client` covers the single render between the record
  // landing and the effect above filling the form from it.
  if (loadStatus === "loading" || (loadStatus === "ready" && !client)) {
    return <ClientDetailSkeleton phase={phase} />;
  }

  // A failed load is not a missing client, and saying so would send someone
  // looking for a record that is sitting there behind an error.
  if (loadStatus !== "ready" || !record || !client) {
    const failed = loadStatus === "failed";

    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader
          title={failed ? "Could not load this client" : "Client not found"}
          breadcrumbs={[
            { label: "My clients", to: ROUTES.myClients },
            { label: failed ? "Client unavailable" : "Client not found" },
          ]}
        />
        {/* In a card of its own, so the state sits on the page the way the
            form would rather than floating under the header. */}
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center card-surface border border-line bg-white">
          <EmptyState
            {...(failed
              ? failedEmptyState(
                  "We could not load this client",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>
                )
              : notFoundEmptyState(
                  "That client no longer exists",
                  "There is nothing here to edit. They may have been deleted since this link was opened.",
                  // A link and not a button — this goes somewhere. See the
                  // note on `buttonClasses`.
                  <Link
                    to={ROUTES.myClients}
                    className={buttonClasses({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Back to my clients
                  </Link>
                ))}
          />
        </div>
      </div>
    );
  }

  const name = fullNameOf(record);

  return (
    // No bottom padding on purpose: the action bar sits flush on the foot of
    // the shell, and the form supplies the space under its own buttons.
    <div className="min-h-full flex flex-col px-page pt-page-top">
      <PageHeader
        title="Edit client"
        breadcrumbs={[
          { label: "My clients", to: ROUTES.myClients },
          // Back to the record rather than to the list: it is where this was
          // opened from, and where cancelling and saving both land.
          { label: name, to: clientDetailPath(record.publicId, "details") },
          { label: "Edit" },
        ]}
      />

      <AddClientForm
        client={client}
        onChange={handleChange}
        errors={errors}
        lookups={lookups}
        saving={saving}
        saveLabel="Save changes"
        onSave={handleSave}
        onCancel={() => navigate(clientDetailPath(record.publicId, "details"))}
        problems={problems}
      />
    </div>
  );
};

export default EditClientPage;
